import { faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { BarcodeSearch } from 'components/barcode';
import { ConfirmationPopup } from 'components/pop-up';
import { ReturnItemQuantityCard, VerifyReturnItemDetails } from 'components/verify-returnItem';
import { TripContext } from 'contexts';
import notify from 'devextreme/ui/notify';
import { ScanResultValueType, SecondaryPopUpField } from 'enums';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigation, useParamService, useReturnItemService, useSettingService } from 'services';
import { VerifiedMethod } from 'services/enums';
import { ReturnChangeReason, ReturnItem, Stop } from 'services/types';
import { ScanResult } from 'types';
import { Formatting } from 'utils';

export const VerifyReturnItemPage = () => {

    const { getIntValue } = useParamService
    const { verifyReturnItem, isReturnItemCompleted } = useReturnItemService()
    const { navigateTo } = useNavigation()
    const { getTrainingMode } = useSettingService()
    
    const isTrainingMode: boolean = getTrainingMode()
    const tripContext = useContext(TripContext)

    const stopId: number | undefined = getIntValue('stopId')
    const returnItemId: number | undefined = getIntValue('returnItemId')
    const [isPopupVisible, setPopupVisible] = useState<boolean>(false)

    const stop: Stop = tripContext.stops.find(stop => stop.id === stopId) ?? {} as Stop
    const [returnItem, setReturnItem] = useState<ReturnItem>(tripContext.returnItems.find(returnItem => returnItem.id == returnItemId) ?? {} as ReturnItem)
    const returnChangeReasons: ReturnChangeReason[] = tripContext.returnChangeReasons.toArray() ?? []
    const [selectedReason, setSelectedReason] = useState<ReturnChangeReason>(returnChangeReasons.find(reason => reason.id == returnItem.changeReasonCode) ?? {} as ReturnChangeReason)
    const [quantity, setQuantity] = useState<number>(returnItem.countedQuantity ?? returnItem.returnQuantity)
    const [isReturnStickerAvailable, setIsReturnStickerAvailable] = useState<boolean>(returnItem.reasonForNoSticker == null ? true : false)

    useEffect(() => {
        if(isTrainingMode) {
            notify(`Scan your next return item to continue`, 'success', 5000)
        }
    }, [])

    const onQuantityAdjusted = (quantity: number) => {

        let newReturnItem: ReturnItem = {} as ReturnItem
        if (quantity == 0) {
            newReturnItem = {
                ...returnItem,
                returnSticker: null,
                reasonForNoSticker: null,
                changeReasonCode: null,
                countedQuantity: quantity
            }
        }
        else
            newReturnItem = { ...returnItem, countedQuantity: quantity, changeReasonCode: returnItem.returnQuantity == quantity ? null : returnItem.changeReasonCode }

        setQuantity(quantity)
        returnItem.returnQuantity == quantity && setSelectedReason({} as ReturnChangeReason)
        completeReturnItem(newReturnItem)
    }

    const onValueChanged = (returnStickerOption: SecondaryPopUpField, value: string | number | null) => {

        let newReturnItem: ReturnItem = {} as ReturnItem
        if (returnStickerOption == SecondaryPopUpField.ReturnSticker) {
            const sticker = value != null ? +value : 0
            newReturnItem = { ...returnItem, returnSticker: sticker > 0 ? sticker : null, reasonForNoSticker: null }
            setIsReturnStickerAvailable(true)
        }
        else if (returnStickerOption == SecondaryPopUpField.NoReturnSticker) {
            newReturnItem = { ...returnItem, reasonForNoSticker: value?.toString()?.trim() ?? null, returnSticker: null }
            setIsReturnStickerAvailable(false)
        }
        completeReturnItem(newReturnItem)
    }

    const onValueReasonChanged = (reason: ReturnChangeReason) => {

        setSelectedReason(reason)
        if (reason.isSalesApprovalRequired)
            setPopupVisible(true)
        let newReturnItem: ReturnItem = { ...returnItem, changeReasonCode: reason.id }
        completeReturnItem(newReturnItem)
    }

    const onClickSalesApprovalCancel = () => {
        setPopupVisible(false)
        setSelectedReason({} as ReturnChangeReason)
        let newReturnItem: ReturnItem = { ...returnItem, changeReasonCode: null }
        completeReturnItem(newReturnItem)
    }

    const renderPhone = (): string => {
        let phone = 'N/A'
        if (returnItem.internalSalesRepPhone) {
            phone = Formatting.formatPhone(returnItem.internalSalesRepPhone)
            if (returnItem.internalSalesRepExt) {
                phone = `${phone}, ext. ${returnItem.internalSalesRepExt}`
            }
        }
        return phone
    }

    let completeReturnItem = (returnItem: ReturnItem): void => {
        let newReturnItem: ReturnItem = { ...returnItem, isChangeComplete: isReturnItemCompleted(returnItem) }
        setReturnItem(newReturnItem)
        tripContext.returnItems.put(newReturnItem)
    }

    const onItemScanned = (result: ScanResult): void => {
        const returnItemIndex: number = result.values.findIndex(value => value.type == ScanResultValueType.ReturnItem)
        const item: ReturnItem = result.values[returnItemIndex].value as ReturnItem
        verifyReturnItem(item, VerifiedMethod.Scanned)
        navigateTo(`verify-returnitem?stopId=${stopId}&returnItemId=${item.id}`)
    }

    return (
        <Fragment>
            <BarcodeSearch tripId={stop.tripId} stopId={stopId} onItemScanned={onItemScanned} />
            {returnItemId &&
                <Fragment>
                    <VerifyReturnItemDetails returnItemId={returnItemId} />
                    <ReturnItemQuantityCard
                        isReturnStickerAvailable={isReturnStickerAvailable}
                        returnItem={returnItem}
                        selectedReason={selectedReason}
                        quantity={quantity}
                        onQuantityAdjusted={(id, quantity) => onQuantityAdjusted(quantity)}
                        onValueChanged={onValueChanged}
                        onValueReasonChanged={onValueReasonChanged}
                    />
                </Fragment>
            }
            <ConfirmationPopup
                isVisible={isPopupVisible}
                title="Confirmation"
                message={`This requires sales approval. Please call: ${returnItem.internalSalesRep} @ ${renderPhone()}`}
                button1={{ icon: faTimes, text: "Cancel", backgroundColor: '#525252' }}
                button2={{ icon: faThumbsUp, text: "Done", backgroundColor: '#82BC24' }}
                onClickButton1={onClickSalesApprovalCancel}
                onClickButton2={() => setPopupVisible(false)}
            />
        </Fragment>
    )
}
